import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Card } from '@amzn/stencil-react-components/card'
import { FileUpload } from '@amzn/stencil-react-components/file-upload'
import { Col, Row, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { H2, Text } from '@amzn/stencil-react-components/text'

import {
    ParsedSpreadsheet,
    pluralize,
    SpreadsheetValidation,
} from 'src/pages/import-content/ImportContentModel'

export interface UploadStepProps {
    isLoadingModule: boolean
    loadError?: string
    moduleName?: string
    placeholders: string[]
    existingContainerNames: string[]
    spreadsheet?: ParsedSpreadsheet
    validation?: SpreadsheetValidation
    onFileAttached: (file?: File) => void
    onDownloadSample: () => void
}

export const UploadStep = ({
    isLoadingModule,
    loadError,
    moduleName,
    placeholders,
    existingContainerNames,
    spreadsheet,
    validation,
    onFileAttached,
    onDownloadSample,
}: UploadStepProps) => (
    <Card isElevated width='100%' dataTestId='import-content-upload-step'>
        <Col gridGap='S300' width='100%'>
            <Row alignItems='center' gridGap='S300' width='100%'>
                <H2 fontSize='T300'>Upload your spreadsheet</H2>
                {isLoadingModule && <Spinner dataTestId='load-template-spinner' />}
            </Row>

            {loadError && (
                <MessageBanner type={MessageBannerType.Error} dataTestId='load-template-error'>
                    {loadError}
                </MessageBanner>
            )}

            {moduleName && (
                <Col gridGap='S200' dataTestId='template-module-info'>
                    <Text fontSize='T200' color='neutral70'>
                        Placeholders ({placeholders.length})
                    </Text>
                    <View
                        dataTestId='template-placeholders'
                        style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
                    >
                        {placeholders.map((name) => (
                            <View
                                key={name}
                                backgroundColor='neutral05'
                                borderRadius={16}
                                padding={{
                                    top: 'S100',
                                    bottom: 'S100',
                                    left: 'S300',
                                    right: 'S300',
                                }}
                            >
                                <Text fontSize='T100' color='neutral90'>
                                    {name}
                                </Text>
                            </View>
                        ))}
                    </View>
                    <Row>
                        <Button
                            dataTestId='download-sample-csv'
                            variant={ButtonVariant.Tertiary}
                            onClick={onDownloadSample}
                        >
                            Download sample CSV
                        </Button>
                    </Row>
                </Col>
            )}

            {existingContainerNames.length > 0 && (
                <MessageBanner
                    type={MessageBannerType.Warning}
                    dataTestId='existing-containers-warning'
                >
                    The following Item Container module name(s) already exist and will be
                    overwritten when you create: {existingContainerNames.join(', ')}
                </MessageBanner>
            )}

            {moduleName && (
                <Col gridGap='S300' width='100%'>
                    <FileUpload
                        id='import-content-file-upload'
                        dragAndDropText='Drag and drop your spreadsheet here'
                        uploadButtonTitle='Upload file'
                        uploadButtonVariant={ButtonVariant.Primary}
                        accept='text/csv'
                        errorText='The upload failed'
                        isMulti={false}
                        onFileAttached={(files) => onFileAttached(files[0])}
                    />
                    <Text fontSize='T100' color='neutral70'>
                        .CSV files supported. Columns must match the placeholders above — use
                        “Download sample CSV” for the exact headers. You move on to Review as soon
                        as they do.
                    </Text>

                    {spreadsheet && (
                        <Col gridGap='S200' dataTestId='csv-validation'>
                            <Text fontSize='T200' color='neutral90'>
                                {pluralize(spreadsheet.rowCount, 'row')} →{' '}
                                {pluralize(spreadsheet.containerCount, 'Item Container module')}
                            </Text>
                            {validation && validation.missing.length > 0 ? (
                                <MessageBanner
                                    type={MessageBannerType.Error}
                                    isDismissible={false}
                                    dataTestId='csv-missing-columns'
                                >
                                    Missing column(s) for placeholder(s):{' '}
                                    {validation.missing.join(', ')}
                                </MessageBanner>
                            ) : (
                                <MessageBanner
                                    type={MessageBannerType.Success}
                                    isDismissible={false}
                                    dataTestId='csv-columns-ok'
                                >
                                    All placeholders have a matching column.
                                </MessageBanner>
                            )}
                            {validation && validation.extra.length > 0 && (
                                <MessageBanner
                                    type={MessageBannerType.Informational}
                                    isDismissible={false}
                                    dataTestId='csv-extra-columns'
                                >
                                    Extra column(s) not used by the template:{' '}
                                    {validation.extra.join(', ')}
                                </MessageBanner>
                            )}
                        </Col>
                    )}
                </Col>
            )}
        </Col>
    </Card>
)
